import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Store, ShoppingBag, Settings2, Check, Loader2 } from "lucide-react";

const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"];
const TIMEZONES = ["America/Denver", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Australia/Sydney"];
const EXP_ACTIONS = [
  { v: "return", l: "Return to consignor" }, { v: "donate", l: "Donate" },
  { v: "markdown", l: "Mark down" }, { v: "extend", l: "Extend" }, { v: "custom", l: "Custom" },
];

const Input = (p) => <input {...p} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />;
const Select = ({ children, ...p }) => <select {...p} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm bg-white outline-none focus:border-emerald-500">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

export default function Onboarding() {
  const navigate = useNavigate();
  const { loadStores } = useStore();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", owner_name: user?.name || "", email: user?.email || "",
    currency: "USD", timezone: "America/Denver", platform: "csv",
    default_payout_pct: 50, default_period_days: 90, expiration_action: "return",
    discount_mode: "reduces_consignor",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stores", f);
      localStorage.setItem("cc_store_id", data.id);
      await loadStores();
      await refreshUser();
      navigate("/dashboard");
    } finally { setLoading(false); }
  };

  const steps = [
    { n: 1, label: "Store Info", icon: Store },
    { n: 2, label: "Platform", icon: ShoppingBag },
    { n: 3, label: "Rules", icon: Settings2 },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f5] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Set up your store</h1>
          <p className="text-slate-500 mt-1">Three quick steps to get you closing books.</p>
        </div>
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step >= s.n ? "bg-emerald-700 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />} {s.label}
              </div>
              {i < steps.length - 1 && <div className={`h-0.5 w-8 ${step > s.n ? "bg-emerald-700" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="onboarding-card">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-slate-900">Store Information</h2>
              <div><Label>Store name</Label><Input data-testid="ob-store-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Willow & Pine Boutique" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Owner / manager</Label><Input data-testid="ob-owner" value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} /></div>
                <div><Label>Email</Label><Input data-testid="ob-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Currency</Label><Select data-testid="ob-currency" value={f.currency} onChange={(e) => set("currency", e.target.value)}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</Select></div>
                <div><Label>Time zone</Label><Select data-testid="ob-timezone" value={f.timezone} onChange={(e) => set("timezone", e.target.value)}>{TIMEZONES.map((t) => <option key={t}>{t}</option>)}</Select></div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-slate-900">Choose your platform</h2>
              <p className="text-slate-500 text-sm">You can connect live credentials later. Everything works with CSV/manual data today.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {[{ v: "shopify", l: "Shopify" }, { v: "woocommerce", l: "WooCommerce" }, { v: "csv", l: "CSV / Manual" }].map((p) => (
                  <button key={p.v} data-testid={`ob-platform-${p.v}`} onClick={() => set("platform", p.v)}
                    className={`p-4 rounded-xl border-2 text-left transition ${f.platform === p.v ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <p className="font-semibold text-slate-900">{p.l}</p>
                    <p className="text-xs text-slate-500 mt-1">{p.v === "csv" ? "Import spreadsheets" : "Connect your store"}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold text-slate-900">Consignment Rules</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Default payout %</Label><Input data-testid="ob-payout" type="number" value={f.default_payout_pct} onChange={(e) => set("default_payout_pct", Number(e.target.value))} /></div>
                <div><Label>Consignment period (days)</Label><Input data-testid="ob-period" type="number" value={f.default_period_days} onChange={(e) => set("default_period_days", Number(e.target.value))} /></div>
              </div>
              <div><Label>When an item expires</Label>
                <Select data-testid="ob-exp-action" value={f.expiration_action} onChange={(e) => set("expiration_action", e.target.value)}>
                  {EXP_ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                </Select>
              </div>
              <div><Label>How discounts affect payouts</Label>
                <Select data-testid="ob-discount-mode" value={f.discount_mode} onChange={(e) => set("discount_mode", e.target.value)}>
                  <option value="reduces_consignor">Discount reduces consignor share</option>
                  <option value="store_absorbs">Store absorbs the discount</option>
                </Select>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button data-testid="ob-back" disabled={step === 1} onClick={() => setStep((s) => s - 1)}
              className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 text-sm font-medium">Back</button>
            {step < 3 ? (
              <button data-testid="ob-next" disabled={step === 1 && !f.name} onClick={() => setStep((s) => s + 1)}
                className="px-6 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm disabled:opacity-50">Continue</button>
            ) : (
              <button data-testid="ob-finish" onClick={finish} disabled={loading}
                className="px-6 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create store
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
